import { createSelector, createSlice } from '@reduxjs/toolkit'
import { WindowSize } from '../../constants/enums'
import { LinkFieldsFragment } from '../../graphql'
import { getInitialWindowSize } from '../../utils/windowSize'

type CommonState = {
  currentRouteKey: string
  windowSize: WindowSize
  brandData?: LinkFieldsFragment
  header: 'normal' | 'search'
  nativeAppMessage: NativeAppMessageReceiveData | null
  iapPrices: {
    sku: string
    amount: number
  }[]
  tenantName?: string
  lazyLoading: {
    slug: string
    hasMore: boolean
  }[]
  brandColor?: string
}

export const initialState: CommonState = {
  currentRouteKey: '',
  windowSize: getInitialWindowSize(),
  brandData: undefined,
  header: 'normal',
  nativeAppMessage: null,
  iapPrices: [],
  tenantName: undefined,
  lazyLoading: [],
  brandColor: undefined,
}

export const commonSlice = createSlice({
  name: 'base/common',
  initialState,
  reducers: {
    setCurrentRouteKey: (state, action) => {
      state.currentRouteKey = action.payload
    },
    setWindowSize: (state, action) => {
      state.windowSize = action.payload
    },
    setBrandData: (state, action) => {
      state.brandData = action.payload
    },
    setHeader: (state, action) => {
      state.header = action.payload
    },
    setNativeAppMessage: (state, action) => {
      state.nativeAppMessage = action.payload
    },
    setIapPrices: (
      state,
      action: {
        payload: {
          sku: string
          amount: number
        }[]
      },
    ) => {
      state.iapPrices = action.payload
    },
    setTenantName: (state, action) => {
      state.tenantName = action.payload
    },
    setLazyLoading: (
      state,
      action: {
        payload: {
          slug: string
          hasMore: boolean
        }[]
      },
    ) => {
      state.lazyLoading = action.payload
    },
    setBrandColor: (state, action) => {
      state.brandColor = action.payload
    },
  },
})

export const selectWindowSize = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.windowSize
  },
  (windowSize) => windowSize,
)

export const selectBrandData = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.brandData
  },
  (brandData) => brandData,
)

export const selectBrandColor = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.brandColor
  },
  (brandColor) => brandColor,
)

export const selectHeader = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.header
  },
  (header) => header,
)

export const selectNativeAppMessage = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.nativeAppMessage
  },
  (nativeAppMessage) => nativeAppMessage,
)

export const selectIapPrices = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.iapPrices
  },
  (iapPrices) => iapPrices,
)

export const selectTenantName = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.tenantName
  },
  (tenantName) => tenantName,
)

export const selectLazyLoading = createSelector(
  (state: { base: { common: CommonState } }) => {
    return state.base.common.lazyLoading
  },
  (lazyLoading) => lazyLoading,
)

export const {
  setCurrentRouteKey,
  setWindowSize,
  setBrandData,
  setHeader,
  setNativeAppMessage,
  setIapPrices,
  setTenantName,
  setLazyLoading,
  setBrandColor,
} = commonSlice.actions

export default commonSlice.reducer
